import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunsList from 'app/components/Reconciliations/ReconciliationRunsList';

import { defaultPageSize } from 'app/constants/table';

import useFetchSalaryReconciliationsPage from 'app/hooks/useFetchSalaryReconciliationsPage';
import useQueueSalaryReconciliation from 'app/hooks/useQueueSalaryReconciliation';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import {
  SalaryReconciliationRunRow,
  SalaryReconciliationRunStatus,
} from 'app/types/salaryReconciliations';

import { getSalaryReconciliationProfilePath } from 'app/utils/routes';
import getSalaryReconciliationColumns from 'app/utils/table/salaryReconciliations';

/**
 * @description Says what a salary accrual reconciliation checks, for the companies in scope.
 */
const describeSalaryRun = (scope: string) =>
  `Check the salary accrual balances of ${scope} against what they should be now? It runs in the background and appears in the list once it starts.`;

/** Lists the salary accrual reconciliation runs, for every company or for the company being managed. */
function SalaryReconciliationRuns() {
  const navigate = useNavigate();
  const company = useManagingCompanyStore((state) => state.company);
  const view = useNavigationStore((state) => state.view);
  const companyId = view === 'company' ? company?.id : undefined;
  const isQueryEnabled = view === 'global' || Boolean(companyId);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [statusFilter, setStatusFilter] = useState<SalaryReconciliationRunStatus[]>([]);

  const {
    data: runs,
    isLoading,
    isRefetching,
    refetch,
  } = useFetchSalaryReconciliationsPage(
    {
      companyId,
      status: statusFilter,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    isQueryEnabled
  );
  const { mutate: queueReconciliation, isPending: isQueueing } = useQueueSalaryReconciliation();

  const handleStatusChange = (statuses: SalaryReconciliationRunStatus[]) => {
    setStatusFilter(statuses);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleSelect = (run: SalaryReconciliationRunRow) =>
    navigate(getSalaryReconciliationProfilePath(run.id));

  return (
    <ReconciliationRunsList
      runs={runs}
      isLoading={isLoading}
      isRefreshing={isRefetching}
      onRefresh={() => refetch()}
      statusFilter={statusFilter}
      onStatusFilterChange={handleStatusChange}
      pagination={pagination}
      onPaginationChange={setPagination}
      columns={getSalaryReconciliationColumns(handleSelect)}
      companyId={companyId}
      companyName={company?.name}
      canRun={isQueryEnabled}
      isQueueing={isQueueing}
      onRun={(companyIds, onSettled) => queueReconciliation({ companyIds }, { onSettled })}
      describeRun={describeSalaryRun}
      testIdPrefix="salary-reconciliation"
    />
  );
}

export default SalaryReconciliationRuns;
